// Live speech-to-text using Deepgram streaming via the server's WebSocket.
// Captures mic audio as 16 kHz PCM through an AudioWorklet, streams it up, and
// emits final user utterances. Deepgram handles endpointing, so there's no
// fragile browser-side VAD.

export type VoiceState = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error'

export interface VoiceClientHandlers {
  onState?: (state: VoiceState) => void
  onUserUtterance?: (text: string) => void
  onError?: (message: string) => void
}

const WS_URL = import.meta.env.VITE_ASSISTANT_WS_URL ?? 'ws://localhost:8080'
const MIC_SAMPLE_RATE = 16000

export function isVoiceSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && 'AudioContext' in window
}

const MAX_RECONNECT_ATTEMPTS = 5

export class VoiceClient {
  private ws: WebSocket | null = null
  private stream: MediaStream | null = null
  private ctx: AudioContext | null = null
  private micNode: AudioWorkletNode | null = null

  private active = false
  private paused = false
  private state: VoiceState = 'idle'
  private reconnectAttempts = 0
  private reconnectTimer: number | null = null

  constructor(
    private handlers: VoiceClientHandlers = {},
    private language: string = 'en',
  ) {}

  private wsUrl(): string {
    const sep = WS_URL.includes('?') ? '&' : '?'
    return `${WS_URL}${sep}lang=${encodeURIComponent(this.language)}`
  }

  private setState(state: VoiceState) {
    if (this.state === state) return
    this.state = state
    this.handlers.onState?.(state)
  }

  getState(): VoiceState {
    return this.state
  }

  async start(): Promise<void> {
    if (!isVoiceSupported()) {
      this.handlers.onError?.('Voice needs a modern browser with microphone support.')
      this.setState('error')
      return
    }
    this.setState('connecting')
    try {
      await this.setupMic()
      await this.connect()
      this.active = true
      this.setState('listening')
    } catch (err) {
      this.handlers.onError?.(err instanceof Error ? err.message : 'Could not start voice chat.')
      this.setState('error')
      this.stop()
    }
  }

  private async setupMic() {
    // Keep echo cancellation (protects against feedback if not on headphones),
    // but disable noise suppression / auto-gain: browsers tune these for phone
    // calls, and they can clip consonants and distort speech in ways that hurt
    // ASR accuracy far more than they help.
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: false, autoGainControl: false },
    })
    this.ctx = new AudioContext({ sampleRate: MIC_SAMPLE_RATE })
    await this.ctx.audioWorklet.addModule('/audio/mic-processor.js')
    const source = this.ctx.createMediaStreamSource(this.stream)
    this.micNode = new AudioWorkletNode(this.ctx, 'mic-processor')
    this.micNode.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
      if (this.paused) return
      if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(e.data)
    }
    source.connect(this.micNode)
  }

  private connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.wsUrl())
      ws.binaryType = 'arraybuffer'
      this.ws = ws
      ws.onopen = () => {
        this.reconnectAttempts = 0
        resolve()
      }
      ws.onerror = () => reject(new Error('Voice service unavailable. Is the assistant server running?'))
      ws.onclose = () => this.handleSocketClosed()
      ws.onmessage = (e) => this.onServerMessage(e.data)
    })
  }

  /**
   * The connection can drop during a long silent pause (e.g. while the mic is
   * paused during a spoken reply). Reconnect transparently instead of going
   * silent — the alternative is exactly the "speaks once, then never again" bug.
   */
  private handleSocketClosed() {
    if (!this.active) return
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this.handlers.onError?.('Lost connection to the voice service. Please try again.')
      this.setState('error')
      this.stop()
      return
    }
    this.reconnectAttempts++
    this.reconnectTimer = window.setTimeout(() => {
      if (!this.active) return
      this.connect()
        .then(() => {
          if (this.active && !this.paused) this.setState('listening')
        })
        .catch(() => this.handleSocketClosed())
    }, 400)
  }

  private onServerMessage(raw: string) {
    let msg: Record<string, any>
    try {
      msg = JSON.parse(raw)
    } catch {
      return
    }
    if (msg.type === 'error') {
      this.handlers.onError?.(msg.message)
      return
    }
    if (msg.type === 'utterance') {
      const text: string = (msg.text ?? '').trim()
      console.debug('[voice] utterance from server:', JSON.stringify(text), 'paused=', this.paused)
      if (text && !this.paused) this.handlers.onUserUtterance?.(text)
    }
  }

  /** Pause sending audio (e.g. while the assistant speaks) to avoid feedback. */
  pause(): void {
    this.paused = true
    console.debug('[voice] mic paused')
  }

  resume(): void {
    if (!this.active) return
    this.paused = false
    this.setState('listening')
    console.debug('[voice] mic resumed')
  }

  setBusy(state: 'thinking' | 'speaking' | 'listening'): void {
    if (this.active) this.setState(state)
  }

  stop(): void {
    this.active = false
    this.paused = false
    if (this.reconnectTimer != null) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      this.ws.onclose = null
      try {
        this.ws.close()
      } catch {
        /* ignore */
      }
      this.ws = null
    }
    this.micNode?.disconnect()
    this.micNode = null
    this.stream?.getTracks().forEach((t) => t.stop())
    this.stream = null
    this.ctx?.close().catch(() => {})
    this.ctx = null
    this.setState('idle')
  }
}
